const RECA_PRODUCT_FORM_SELECTOR = "product-form[data-reca-size-required]";
const RECA_SIZE_SELECTED_FORM_IDS = new Set();
const RECA_BOUND_FORMS = new WeakSet();

// A product form contains a hidden `<input name="id">`, and named form controls
// shadow the element's own properties, so `form.id` returns that input element
// rather than the form's id string. Always read the attribute instead.
function getFormId(form) {
  return form.getAttribute("id");
}

function getCartForm(productForm) {
  return productForm.querySelector('form[action*="/cart/add"]');
}

function getRelatedBuyButtons(form) {
  const formId = getFormId(form);

  if (!formId) {
    return [];
  }

  return Array.from(document.querySelectorAll("buy-buttons[form]"))
    .filter((buttons) => buttons.getAttribute("form") === formId);
}

function setText(element, text) {
  if (element.textContent.trim() !== text) {
    element.textContent = text;
  }
}

function setData(element, key, value) {
  if (element.dataset[key] !== value) {
    element.dataset[key] = value;
  }
}

function syncButtons(productForm) {
  const form = getCartForm(productForm);

  if (!form) {
    return;
  }

  // The Set is the source of truth: the data attribute is wiped whenever the
  // theme re-renders the section after a variant change.
  const sizeSelected = RECA_SIZE_SELECTED_FORM_IDS.has(getFormId(form));
  const variantPending = productForm.dataset.recaVariantPending === "true";

  setData(productForm, "recaSizeSelected", sizeSelected ? "true" : "false");

  const relatedBuyButtons = getRelatedBuyButtons(form);
  const selectLabel = relatedBuyButtons[0]?.dataset.recaSelectLabel || "Select a size";

  relatedBuyButtons.forEach((buttons) => {
    const submitButton = buttons.querySelector('button[type="submit"]');

    if (!submitButton) {
      return;
    }

    const activeLabel = buttons.dataset.recaActiveLabel || "Add to cart";
    const activeDisabled = buttons.dataset.recaActiveDisabled === "true";
    const buttonSelectLabel = buttons.dataset.recaSelectLabel || selectLabel;

    if (!sizeSelected || variantPending) {
      submitButton.disabled = true;
      setText(submitButton, buttonSelectLabel);
      return;
    }

    submitButton.disabled = activeDisabled;
    setText(submitButton, activeLabel);
  });
}

function handleSizeLabelClick(event) {
  const label = event.target.closest?.("label");

  if (!label || !label.closest("[data-reca-size-option]") || !label.htmlFor || label.classList.contains("is-disabled")) {
    return;
  }

  const input = document.getElementById(label.htmlFor);

  if (!input?.matches('input[type="radio"][data-option-position]') || input.disabled) {
    return;
  }

  const form = input.form;
  const productForm = form?.closest(RECA_PRODUCT_FORM_SELECTOR);

  if (!productForm) {
    return;
  }

  // Record intentional size choices while leaving label activation and the
  // theme's native change handler in control of the radio input and variant.
  RECA_SIZE_SELECTED_FORM_IDS.add(getFormId(form));
  setData(productForm, "recaVariantPending", input.checked ? "false" : "true");

  // Clicking the already-selected size does not emit a native change event,
  // so synchronise the button state here. For a new size, the native label
  // click will emit change and the existing handler will resolve the variant.
  syncButtons(productForm);
}

function handleVariantSettled(event) {
  const form = event.currentTarget;
  const productForm = form.closest(RECA_PRODUCT_FORM_SELECTOR);

  if (!productForm) {
    return;
  }

  setData(productForm, "recaVariantPending", "false");
  syncButtons(productForm);
  requestAnimationFrame(() => syncButtons(productForm));
}

function initialiseProductForm(productForm) {
  const form = getCartForm(productForm);

  if (!form) {
    return;
  }

  // Bind against the form element itself so a section re-render, which clears
  // the data attributes, cannot cause duplicate listeners.
  if (!RECA_BOUND_FORMS.has(form)) {
    RECA_BOUND_FORMS.add(form);
    form.addEventListener("variant:change", handleVariantSettled);
    form.addEventListener("product:rerender", handleVariantSettled);
  }

  setData(productForm, "recaSizeInitialised", "true");
  syncButtons(productForm);
}

function initialiseAllProductForms() {
  document.querySelectorAll(RECA_PRODUCT_FORM_SELECTOR).forEach(initialiseProductForm);
}

function resetQuickBuySizeSelection(modal) {
  modal.querySelectorAll(RECA_PRODUCT_FORM_SELECTOR).forEach((productForm) => {
    const form = getCartForm(productForm);

    if (!form) {
      return;
    }

    RECA_SIZE_SELECTED_FORM_IDS.delete(getFormId(form));
    setData(productForm, "recaVariantPending", "false");
    syncButtons(productForm);
  });
}

function handleQuickBuyReset(event) {
  const modal = event.target;

  if (!(modal instanceof Element) || !modal.matches("quick-buy-modal")) {
    return;
  }

  resetQuickBuySizeSelection(modal);
}

document.addEventListener("change", (event) => {
  const input = event.target.closest?.("input[data-option-position]");
  const form = input?.form;

  if (!form) {
    return;
  }

  const productForm = form.closest(RECA_PRODUCT_FORM_SELECTOR);

  if (!productForm) {
    return;
  }

  if (input.closest("[data-reca-size-option]")) {
    RECA_SIZE_SELECTED_FORM_IDS.add(getFormId(form));
  }

  setData(productForm, "recaVariantPending", "true");
  syncButtons(productForm);
}, true);

document.addEventListener("click", handleSizeLabelClick, true);
document.addEventListener("dialog:before-show", handleQuickBuyReset, true);
document.addEventListener("dialog:after-hide", handleQuickBuyReset, true);

new MutationObserver(initialiseAllProductForms).observe(document.documentElement, {
  childList: true,
  subtree: true
});

initialiseAllProductForms();
